import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenantStore } from '@/store/tenantStore';
import { translateDynamicContent } from '@/i18n';
import styles from './DataPrivacyDialog.module.css';
import { sanitizeHtml } from '@/utils/sanitizeHtml';

/**
 * DataPrivacyDialog — consent prompt shown before a resume is uploaded for analysis.
 * Mirrors Flutter CustomDialog.showDataPrivacy (title + TermsAndConditionsTextWidget
 * + Cancel / Agree, then a dotted rule and the accepted formats line).
 * Links pointing to 'dialog' or '#' open the tenant privacy policy.
 * Both tenant texts go through the language file's `dynamicContent` map, like Flutter.
 */

interface DataPrivacyDialogProps {
  onAgree: () => void;
  onCancel: () => void;
}

export function DataPrivacyDialog({ onAgree, onCancel }: DataPrivacyDialogProps) {
  const { t } = useTranslation();
  const content = useTenantStore((s) => s.content);
  const [showPolicy, setShowPolicy] = useState(false);

  const termsHtml =
    translateDynamicContent(content?.contentSettings?.termAndConditions?.content ?? '') ||
    t('I have read, and I acknowledge the <a href="dialog">Privacy Policy</a> and <a href="dialog">Terms & Conditions</a>');
  const privacyPolicyHtml =
    translateDynamicContent(content?.contentSettings?.privacyPolicy?.content ?? '') ||
    '<p>Privacy policy content is not available.</p>';

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (showPolicy) setShowPolicy(false);
      else onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showPolicy, onCancel]);

  const handleLinkClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = (e.target as HTMLElement).closest('a');
    if (!target) return;
    e.preventDefault();
    const href = target.getAttribute('href');
    if (href === 'dialog' || href === '#') {
      setShowPolicy(true);
    } else if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  }, []);

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="data-privacy-title">
      <div className={styles.dialog}>
        {showPolicy ? (
          <>
            <button
              type="button"
              className={styles.back}
              onClick={() => setShowPolicy(false)}
            >
              ← {t('Back')}
            </button>
            <div className={styles.policy} dangerouslySetInnerHTML={{ __html: sanitizeHtml(privacyPolicyHtml) }} />
          </>
        ) : (
          <>
            <h2 id="data-privacy-title" className={styles.title}>{t('Data Privacy')}</h2>
            <div
              className={styles.terms}
              onClick={handleLinkClick}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(termsHtml) }}
            />
            <div className={styles.actions}>
              <button type="button" className={styles.cancel} onClick={onCancel}>
                {t('Cancel')}
              </button>
              <button type="button" className={styles.agree} onClick={onAgree} autoFocus>
                {t('Agree')}
              </button>
            </div>
            {/* WebUploadResume.acceptedResumeFormats + supportedFiles (untranslated) + 'less than' + 5MB */}
            <p className={styles.formats}>
              {t('We accept ')}
              <strong>DOC, DOCX, TXT, PDF, WPS</strong> {t('less than')} <strong>5MB</strong>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
